import { focusElement } from 'platform/utilities/ui';
import {
  isEscape,
  isTab,
  isReverseTab,
  getTabbableElements,
} from '../utilities/accessibility';

const INERT_MARKER = 'data-crisis-line-inert';

// The element that opened the modal, focused again when it closes.
let openControl;

/*
 * Sets where focus returns when the modal closes, for code that opens the
 * modal without one of its [data-show="#modal-crisisline"] triggers.
 */
export function setCrisisLineModalReturnFocus(element) {
  openControl = element;
}

/*
 * Makes everything outside the Veterans Crisis Line modal inert while it is
 * open. Tab wrapping alone doesn't stop screen reader reading keys from
 * leaving the modal; inert hides the background from assistive tech.
 * Only elements this function made inert are released.
 */
export function setCrisisLineModalBackgroundInert(isInert) {
  if (!isInert) {
    document.querySelectorAll(`[${INERT_MARKER}]`).forEach(el => {
      el.removeAttribute('inert');
      el.removeAttribute(INERT_MARKER);
    });
    return;
  }

  let node = document.getElementById('modal-crisisline');
  while (node && node !== document.body && node.parentElement) {
    for (const sibling of node.parentElement.children) {
      if (sibling !== node && !sibling.hasAttribute('inert')) {
        sibling.setAttribute('inert', '');
        sibling.setAttribute(INERT_MARKER, '');
      }
    }
    node = node.parentElement;
  }
}

/*
 * Creates function that captures/releases Veterans Crisis Line modal focus.
 */
export default function addFocusBehaviorToCrisisLineModal() {
  const overlay = document.getElementById('modal-crisisline');
  const modal = document.querySelector('.va-crisis-panel.va-modal-inner');
  const tabbableElements = getTabbableElements(modal);
  const closeControl = tabbableElements[0];
  const lastTabbableElement = tabbableElements[tabbableElements.length - 1];
  const triggers = Array.from(
    document.querySelectorAll('[data-show="#modal-crisisline"]'),
  );

  function captureFocus(e) {
    if (e.target === closeControl && isReverseTab(e)) {
      e.preventDefault();
      focusElement(lastTabbableElement);
    }
    if (e.target === lastTabbableElement && isTab(e)) {
      e.preventDefault();
      focusElement(closeControl);
    }
  }

  function closeModal(e) {
    if (isEscape(e)) {
      overlay.classList.remove('va-overlay--open');
      document.body.classList.remove('va-pos-fixed');
      setCrisisLineModalBackgroundInert(false);
      focusElement(openControl);
    }
  }

  function resetFocus() {
    setCrisisLineModalBackgroundInert(false);
    focusElement(openControl);
  }

  // We're saving the element that triggered this modal
  // in openControl, so that we can focus back on it later,
  // when the modal is closed
  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      openControl = trigger;
      setCrisisLineModalBackgroundInert(true);
    });
  });

  modal.addEventListener('keydown', closeModal);
  closeControl.addEventListener('click', resetFocus);
  closeControl.addEventListener('keydown', captureFocus);
  lastTabbableElement.addEventListener('keydown', captureFocus);
}
